import { useCallback, useEffect, useRef, useState } from 'react';
import type { EditedWorkspaceSnapshot } from '../Reports/addFileToReports';

export type WorkspaceSyncStatus = 'idle' | 'unsaved' | 'saved';

export interface UseUnsavedEditsGuardArgs {
    /** Off in contexts that persist on their own (the Reports sheet editor autosaves). */
    enabled: boolean;
    /** The screen differs from the last confirmed Workspace save (or from the original if never added). */
    hasUnsavedEdits: boolean;
    /** Sticky — the screen differs from the original file; an add never resets it. */
    hasEditedSinceOpen: boolean;
    /** Monotonic content-edit counter — lets a successful add tell whether an edit landed after its snapshot. */
    getEditVersion: () => number;
    /** Builds the on-screen content to hand to the host — only called when there are edits. */
    buildSnapshot: () => EditedWorkspaceSnapshot;
    /** Flips the drawer's dirty state back to clean once the host confirms the add. */
    clearUnsavedEdits: () => void;
    onClose: () => void;
    onAddToReports?: (edited?: EditedWorkspaceSnapshot) => void;
    addingToReports?: boolean;
    addedToReports?: boolean;
}

export interface UseUnsavedEditsGuardResult {
    /** Use in place of `onClose` for every close path (×, ESC, backdrop). */
    requestClose: () => void;
    /** The header's Add to Workspace click — hands the host the edited snapshot, or nothing when clean. */
    addToWorkspace: () => void;
    /** Drives the header's saved/unsaved indicator. */
    syncStatus: WorkspaceSyncStatus;
    dialog: {
        open: boolean;
        onKeepEditing: () => void;
        onDiscard: () => void;
        onAddToWorkspace?: () => void;
    };
}

/**
 * Shared "edits must reach Workspace" behaviour for the editable file drawers
 * (SheetEditorDrawer in a view context, DocxDrawer):
 *
 * - `addToWorkspace` passes the live content to the host's `onAddToReports`
 *   once the user has edited (even after an earlier add), and `undefined`
 *   (→ snapshot the original bytes, exactly as before) while the file is
 *   identical to the original (never edited since open).
 * - `requestClose` intercepts every close with unsaved edits and asks the
 *   user to add / keep editing / discard. "Add to Workspace" from that dialog
 *   closes the drawer only once the host reports success; a failed add leaves
 *   the editor open with the edits intact.
 * - The host owns the async add and signals its outcome through the existing
 *   `addingToReports` / `addedToReports` props, so no new callback contract is
 *   needed: a run that ends with `addedToReports` true succeeded, one that
 *   ends without it failed (hosts reset `addedToReports` when an add starts).
 * - A successful add clears "unsaved" only if no edit landed after the snapshot.
 */
export const useUnsavedEditsGuard = ({
    enabled,
    hasUnsavedEdits,
    hasEditedSinceOpen,
    getEditVersion,
    buildSnapshot,
    clearUnsavedEdits,
    onClose,
    onAddToReports,
    addingToReports,
    addedToReports,
}: UseUnsavedEditsGuardArgs): UseUnsavedEditsGuardResult => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [savedToWorkspace, setSavedToWorkspace] = useState(false);
    // Set by the dialog's "Add to Workspace": close once the host confirms.
    const pendingCloseRef = useRef(false);
    // Observed an add start — the next `addingToReports:false` is its outcome.
    const sawAddingRef = useRef(false);
    // Edit counter captured when the add started — compared on success.
    const snapshotVersionRef = useRef<number | null>(null);

    const guardActive = enabled && hasUnsavedEdits;

    const addToWorkspace = useCallback(() => {
        if (!onAddToReports) return;
        snapshotVersionRef.current = getEditVersion();
        onAddToReports(hasEditedSinceOpen ? buildSnapshot() : undefined);
    }, [onAddToReports, hasEditedSinceOpen, buildSnapshot, getEditVersion]);

    const requestClose = useCallback(() => {
        if (guardActive) {
            setDialogOpen(true);
            return;
        }
        onClose();
    }, [guardActive, onClose]);

    useEffect(() => {
        if (addingToReports) {
            sawAddingRef.current = true;
            return;
        }
        if (!sawAddingRef.current) return;
        sawAddingRef.current = false;
        const caughtUp = snapshotVersionRef.current !== null && getEditVersion() === snapshotVersionRef.current;
        snapshotVersionRef.current = null;
        if (addedToReports && caughtUp) {
            clearUnsavedEdits();
            setSavedToWorkspace(true);
            if (pendingCloseRef.current) {
                pendingCloseRef.current = false;
                onClose();
            }
            return;
        }
        // The add failed, or an edit landed mid-flight — stay open, edits stay unsaved (the host toasts a failure).
        pendingCloseRef.current = false;
    }, [addingToReports, addedToReports, clearUnsavedEdits, getEditVersion, onClose]);

    // A fresh edit after a successful add flips the indicator back to unsaved.
    useEffect(() => {
        if (hasUnsavedEdits) setSavedToWorkspace(false);
    }, [hasUnsavedEdits]);

    // Browser tab close / reload with unsaved edits — the drawer can't show its
    // own dialog there, so lean on the native prompt.
    useEffect(() => {
        if (!guardActive) return undefined;
        const warn = (e: BeforeUnloadEvent) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [guardActive]);

    const syncStatus: WorkspaceSyncStatus = !enabled ? 'idle' : hasUnsavedEdits ? 'unsaved' : savedToWorkspace ? 'saved' : 'idle';

    return {
        requestClose,
        addToWorkspace,
        syncStatus,
        dialog: {
            open: dialogOpen,
            onKeepEditing: () => setDialogOpen(false),
            onDiscard: () => {
                setDialogOpen(false);
                onClose();
            },
            onAddToWorkspace: onAddToReports
                ? () => {
                      setDialogOpen(false);
                      pendingCloseRef.current = true;
                      addToWorkspace();
                  }
                : undefined,
        },
    };
};
